import React from "react";
import {
  Alert,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { AssociatedCampaignSync } from "../../store/apis/briefingApi";

interface AssociatedCampaignSyncSectionProps {
  sync: AssociatedCampaignSync | null;
  onCreateSync?: () => void;
  onEditSync?: () => void;
  isEditMode?: boolean;
}

const AssociatedCampaignSyncSection: React.FC<
  AssociatedCampaignSyncSectionProps
> = ({ sync, onCreateSync, onEditSync, isEditMode = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const renderDetailField = (
    label: string,
    value?: string | boolean | null
  ) => (
    <Grid item xs={12} md={4} key={label}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {value === null || value === undefined || value === ""
          ? t("common.noData", "No data available")
          : typeof value === "boolean"
            ? value
              ? t("briefingsView.sync.linked", "Linked")
              : t("briefingsView.sync.notLinked", "Not linked")
            : value}
      </Typography>
    </Grid>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 4 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t("briefingsView.syncSectionTitle", "Linking status")}
        </Typography>
        {sync ? (
          isEditMode && (
            <Button
              variant="outlined"
              onClick={onEditSync}
              disabled={!onEditSync}
            >
              {t("common.edit", "Edit")}
            </Button>
          )
        ) : (
          <Button
            variant="contained"
            onClick={onCreateSync}
            disabled={!onCreateSync}
          >
            {t("briefingsView.createSync", "Link Campaign")}
          </Button>
        )}
      </Stack>

      {!sync ? (
        <Alert severity="info">
          {t(
            "briefingsView.syncNotFound",
            "This associated campaign is not linked yet."
          )}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {renderDetailField(
            t("briefingsView.sync.beapoloCampaignId", "BeApolo campaign ID"),
            sync.beapoloCampaignId ?? "-"
          )}
          {renderDetailField(
            t("briefingsView.sync.internalCampaignId", "Internal campaign ID"),
            sync.internalCampaignId ?? "-"
          )}
          {renderDetailField(
            t("briefingsView.sync.insertionOrderId", "Insertion order ID"),
            sync.insertionOrderId ?? "-"
          )}
          {renderDetailField(
            t("briefingsView.sync.isLinked", "Linked status"),
            sync.isLinked
          )}
          {renderDetailField(
            t("briefingsView.addedOn", "Added on"),
            dayjs(sync.createdAt).format("YYYY-MM-DD HH:mm:ss")
          )}
          {renderDetailField(
            t("briefingsView.updatedOn", "Last updated"),
            dayjs(sync.updatedAt).format("YYYY-MM-DD HH:mm:ss")
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default AssociatedCampaignSyncSection;
